import React from 'react';
import type {
    DOMConversionMap,
    DOMConversionOutput,
    DOMExportOutput,
    EditorConfig,
    LexicalNode,
    NodeKey,
    SerializedLexicalNode,
    Spread,
} from 'lexical';
import { $applyNodeReplacement, DecoratorNode } from 'lexical';

export type SerializedImageNode = Spread<
    {
        src: string;
        altText: string;
        type: 'image';
        version: 1;
    },
    SerializedLexicalNode
>;

function convertImageElement(domNode: Node): DOMConversionOutput | null {
    if (domNode instanceof HTMLImageElement) {
        const { src, alt } = domNode;
        if (!src) return null;
        return { node: $createImageNode({ src, altText: alt || '' }) };
    }
    return null;
}

export class ImageNode extends DecoratorNode<JSX.Element> {
    __src: string;
    __altText: string;

    static getType(): string {
        return 'image';
    }

    static clone(node: ImageNode): ImageNode {
        return new ImageNode(node.__src, node.__altText, node.__key);
    }

    constructor(src: string, altText = '', key?: NodeKey) {
        super(key);
        this.__src = src;
        this.__altText = altText;
    }

    createDOM(_config: EditorConfig): HTMLElement {
        const span = document.createElement('span');
        span.className = 'thspian-lexical-image-wrap';
        return span;
    }

    updateDOM(): false {
        return false;
    }

    exportDOM(): DOMExportOutput {
        const img = document.createElement('img');
        img.setAttribute('src', this.__src);
        img.setAttribute('alt', this.__altText);
        img.setAttribute('style', 'max-width:100%;height:auto;border-radius:6px;');
        return { element: img };
    }

    static importDOM(): DOMConversionMap | null {
        return {
            img: () => ({
                conversion: convertImageElement,
                priority: 0,
            }),
        };
    }

    exportJSON(): SerializedImageNode {
        return {
            type: 'image',
            version: 1,
            src: this.__src,
            altText: this.__altText,
        };
    }

    static importJSON(serialized: SerializedImageNode): ImageNode {
        return $createImageNode({
            src: serialized.src,
            altText: serialized.altText,
        });
    }

    getSrc(): string {
        return this.__src;
    }

    getAltText(): string {
        return this.__altText;
    }

    decorate(): JSX.Element {
        return (
            <img
                src={this.__src}
                alt={this.__altText}
                className="thspian-lexical-image"
                style={{ maxWidth: '100%', height: 'auto', borderRadius: 6, display: '8px 0' }}
            />
        );
    }
}

export function $createImageNode({ src, altText = '' }: { src: string; altText?: string }): ImageNode {
    return $applyNodeReplacement(new ImageNode(src, altText));
}

export function $isImageNode(node: LexicalNode | null | undefined): node is ImageNode {
    return node instanceof ImageNode;
}
